import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { loadError, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Plus, Theater, Check, X, Hourglass } from "lucide-react";
import AuditoriumDialog, { AUD_STATUS, AUD_PAY } from "@/components/AuditoriumDialog";

const pad = (n) => String(n).padStart(2, "0");
const key = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const todayKey = key(new Date());
const DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const HOURS = Array.from({ length: 16 }, (_, i) => i + 7);
const timeLabel = (b) => (b.all_day ? "Tutto il giorno" : `${b.start_time}–${b.end_time}`);

const monthRange = (cur) => {
  const first = new Date(cur.getFullYear(), cur.getMonth(), 1);
  const start = new Date(first); start.setDate(first.getDate() - ((first.getDay() + 6) % 7));
  return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
};
const weekRange = (cur) => {
  const start = new Date(cur); start.setDate(cur.getDate() - ((cur.getDay() + 6) % 7));
  return Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
};

const Chip = ({ b, onClick }) => (
  <button data-testid={`aud-event-${b.id}`} onClick={(e) => { e.stopPropagation(); onClick(b); }}
    className={`w-full text-left rounded-md px-1.5 py-0.5 text-[11px] leading-tight truncate shadow-sm hover:brightness-110 ${AUD_STATUS[b.status].cls}`} title={`${b.title} · ${timeLabel(b)} · ${b.requester_name}`}>
    {!b.all_day && <span className="font-mono opacity-90 mr-1">{b.start_time}</span>}{b.title}
  </button>
);

const MonthView = ({ cur, byDate, onDay, onEvent }) => (
  <div className="rounded-xl border border-border overflow-hidden bg-card" data-testid="aud-month-view">
    <div className="grid grid-cols-7 bg-secondary/60 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      {DAYS.map((d) => <div key={d} className="px-2 py-2 text-center">{d}</div>)}
    </div>
    <div className="grid grid-cols-7">
      {monthRange(cur).map((d) => {
        const k = key(d); const inMonth = d.getMonth() === cur.getMonth(); const evs = byDate[k] || [];
        return (
          <div key={k} data-testid={`aud-day-${k}`} onClick={() => onDay(k)}
            className={`min-h-[104px] border-t border-r border-border p-1.5 cursor-pointer transition-colors hover:bg-accent/10 ${inMonth ? "" : "bg-secondary/30 text-muted-foreground"}`}>
            <div className={`text-xs font-semibold mb-1 w-6 h-6 flex items-center justify-center rounded-full ${k === todayKey ? "bg-accent text-accent-foreground" : ""}`}>{d.getDate()}</div>
            <div className="space-y-0.5">{evs.slice(0, 3).map((b) => <Chip key={b.id} b={b} onClick={onEvent} />)}{evs.length > 3 && <div className="text-[10px] text-muted-foreground px-1">+{evs.length - 3} altre</div>}</div>
          </div>
        );
      })}
    </div>
  </div>
);

const WeekView = ({ cur, byDate, onSlot, onEvent }) => {
  const days = weekRange(cur);
  const pos = (b) => { const [sh, sm] = b.start_time.split(":").map(Number); const [eh, em] = b.end_time.split(":").map(Number); const top = Math.max(0, (sh + sm / 60 - 7) * 48); const h = Math.max(24, ((eh + em / 60) - Math.max(7, sh + sm / 60)) * 48); return { top, height: Math.min(h, HOURS.length * 48 - top) }; };
  return (
    <div className="rounded-xl border border-border overflow-hidden bg-card" data-testid="aud-week-view">
      <div className="grid" style={{ gridTemplateColumns: "56px repeat(7, 1fr)" }}>
        <div className="bg-secondary/60" />
        {days.map((d) => { const k = key(d); return (
          <div key={k} className="bg-secondary/60 px-2 py-2 text-center border-l border-border">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{DAYS[(d.getDay() + 6) % 7]}</div>
            <div className={`text-sm font-semibold inline-flex w-7 h-7 items-center justify-center rounded-full ${k === todayKey ? "bg-accent text-accent-foreground" : ""}`}>{d.getDate()}</div>
            <div className="mt-1 space-y-0.5">{(byDate[k] || []).filter((b) => b.all_day).map((b) => <Chip key={b.id} b={b} onClick={onEvent} />)}</div>
          </div>); })}
        <div>{HOURS.map((h) => <div key={h} className="h-12 text-[10px] text-muted-foreground text-right pr-1.5 pt-0.5 border-t border-border font-mono">{pad(h)}:00</div>)}</div>
        {days.map((d) => { const k = key(d); return (
          <div key={k} className="relative border-l border-border">
            {HOURS.map((h) => <div key={h} data-testid={`aud-slot-${k}-${pad(h)}`} onClick={() => onSlot(k, `${pad(h)}:00`)} className="h-12 border-t border-border hover:bg-accent/10 cursor-pointer" />)}
            {(byDate[k] || []).filter((b) => !b.all_day).map((b) => { const p = pos(b); return (
              <button key={b.id} data-testid={`aud-event-${b.id}`} onClick={() => onEvent(b)} style={{ top: p.top, height: p.height }}
                className={`absolute left-0.5 right-0.5 rounded-md px-1.5 py-1 text-[11px] text-left leading-tight overflow-hidden shadow hover:brightness-110 ${AUD_STATUS[b.status].cls}`}>
                <div className="font-semibold truncate">{b.title}</div><div className="opacity-90 font-mono">{b.start_time}–{b.end_time}</div>
              </button>); })}
          </div>); })}
      </div>
    </div>
  );
};

const PendingPanel = ({ items, onOpen, onDecide, highlight }) => (
  <div className={`rounded-xl border p-4 ${highlight ? "border-amber-500 ring-2 ring-amber-500/30" : "border-amber-500/40"} bg-amber-50 dark:bg-amber-900/15`} data-testid="aud-pending-panel">
    <h3 className="font-heading text-base mb-3 flex items-center gap-2"><Hourglass className="w-4 h-4 text-amber-600" />Richieste da approvare <span className="rounded-full bg-amber-500 text-white text-xs px-2 py-0.5" data-testid="aud-pending-count">{items.length}</span></h3>
    {items.length === 0 ? <p className="text-sm text-muted-foreground">Nessuna richiesta in attesa.</p> : (
      <ul className="divide-y divide-amber-500/20 text-sm">
        {items.map((b) => (
          <li key={b.id} className="py-2 flex flex-wrap items-center gap-x-4 gap-y-1" data-testid={`aud-pending-${b.id}`}>
            <button className="font-mono text-xs text-muted-foreground w-28 text-left hover:text-accent" onClick={() => onOpen(b)}>{new Date(b.date + "T00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" })} · {b.all_day ? "intera" : b.start_time}</button>
            <button className="font-medium hover:text-accent text-left" onClick={() => onOpen(b)}>{b.title}</button>
            <span className="text-muted-foreground">{b.requester_name}{b.phone ? ` · ${b.phone}` : ""}{b.participants ? ` · ${b.participants} pers.` : ""}</span>
            <div className="ml-auto flex gap-1.5">
              <Button size="sm" data-testid={`aud-approve-${b.id}`} onClick={() => onDecide(b, "confirmed")} className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white"><Check className="w-3.5 h-3.5 mr-1" />Conferma</Button>
              <Button size="sm" variant="outline" data-testid={`aud-reject-${b.id}`} onClick={() => onDecide(b, "cancelled")} className="h-7 text-red-600 hover:text-red-700"><X className="w-3.5 h-3.5 mr-1" />Rifiuta</Button>
            </div>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default function Auditorium() {
  const [params] = useSearchParams();
  const showPending = params.get("pending") === "1";
  const [cur, setCur] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });
  const [view, setView] = useState(() => localStorage.getItem("aud_view") || "month");
  const [items, setItems] = useState([]);
  const [pending, setPending] = useState([]);
  const [dlg, setDlg] = useState({ open: false, booking: null, date: "", time: "" });

  const loadPending = () => api.get("/auditorium", { params: { start: todayKey } }).then((x) => setPending(x.data.filter((b) => b.status === "requested"))).catch(loadError);
  const load = () => {
    const r = view === "month" ? monthRange(cur) : weekRange(cur);
    api.get("/auditorium", { params: { start: key(r[0]), end: key(r[r.length - 1]) } }).then((x) => setItems(x.data)).catch(loadError);
    loadPending();
  };
  useEffect(load, [cur, view]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => localStorage.setItem("aud_view", view), [view]);

  const decide = async (b, status) => {
    if (status === "cancelled" && !window.confirm(`Rifiutare la richiesta "${b.title}"?`)) return;
    try {
      await api.patch(`/auditorium/${b.id}/status`, { status });
      toast.success(status === "confirmed" ? `"${b.title}" confermata` : `"${b.title}" rifiutata`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const byDate = useMemo(() => items.reduce((acc, b) => ((acc[b.date] ||= []).push(b), acc), {}), [items]);
  const move = (n) => setCur((c) => { const d = new Date(c); if (view === "month") { d.setDate(1); d.setMonth(d.getMonth() + n); } else d.setDate(d.getDate() + 7 * n); return d; });
  const title = view === "month" ? cur.toLocaleDateString("it-IT", { month: "long", year: "numeric" }) : `${weekRange(cur)[0].toLocaleDateString("it-IT", { day: "numeric", month: "short" })} – ${weekRange(cur)[6].toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" })}`;
  const upcoming = items.filter((b) => b.date >= todayKey && b.status !== "cancelled").slice(0, 6);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">Spazi & Eventi</p>
          <h1 className="font-display text-4xl sm:text-5xl mt-1">Calendario Prenotazioni Auditorium</h1>
        </div>
        <Button data-testid="aud-new-button" onClick={() => setDlg({ open: true, booking: null, date: todayKey, time: "" })} className="bg-accent hover:bg-accent/90 text-accent-foreground rounded-full h-10 px-5 font-heading"><Plus className="w-4 h-4 mr-2" />Prenota auditorium</Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" data-testid="aud-prev" onClick={() => move(-1)}><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" size="sm" data-testid="aud-today" onClick={() => setCur(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; })}>Oggi</Button>
          <Button variant="outline" size="icon" data-testid="aud-next" onClick={() => move(1)}><ChevronRight className="w-4 h-4" /></Button>
        </div>
        <h2 className="font-heading text-lg capitalize" data-testid="aud-period">{title}</h2>
        <div className="ml-auto inline-flex rounded-full border border-border p-0.5 text-sm">
          {[["month", "Mese"], ["week", "Settimana"]].map(([k, l]) => (
            <button key={k} data-testid={`aud-view-${k}`} onClick={() => setView(k)} className={`rounded-full px-4 py-1 transition-colors ${view === k ? "bg-accent text-accent-foreground" : "hover:bg-secondary"}`}>{l}</button>
          ))}
        </div>
        <div className="flex gap-2 text-xs">{Object.entries(AUD_STATUS).map(([k, v]) => <span key={k} className={`rounded-full px-2.5 py-0.5 ${v.soft}`}>{v.label}</span>)}</div>
      </div>

      {(showPending || pending.length > 0) && <PendingPanel items={pending} highlight={showPending} onOpen={(b) => setDlg({ open: true, booking: b, date: "", time: "" })} onDecide={decide} />}

      {view === "month"
        ? <MonthView cur={cur} byDate={byDate} onDay={(d) => setDlg({ open: true, booking: null, date: d, time: "" })} onEvent={(b) => setDlg({ open: true, booking: b, date: "", time: "" })} />
        : <WeekView cur={cur} byDate={byDate} onSlot={(d, t) => setDlg({ open: true, booking: null, date: d, time: t })} onEvent={(b) => setDlg({ open: true, booking: b, date: "", time: "" })} />}

      <div className="rounded-xl border border-border bg-card p-4" data-testid="aud-upcoming">
        <h3 className="font-heading text-base mb-3 flex items-center gap-2"><Theater className="w-4 h-4 text-accent" />Prossime prenotazioni nel periodo</h3>
        {upcoming.length === 0 ? <p className="text-sm text-muted-foreground">Nessuna prenotazione in programma.</p> : (
          <ul className="divide-y divide-border text-sm">
            {upcoming.map((b) => (
              <li key={b.id} className="py-2 flex flex-wrap items-center gap-x-4 gap-y-1 cursor-pointer hover:text-accent" onClick={() => setDlg({ open: true, booking: b, date: "", time: "" })} data-testid={`aud-upcoming-${b.id}`}>
                <span className="font-mono text-xs text-muted-foreground w-24">{new Date(b.date + "T00:00").toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" })} · {b.all_day ? "intera" : b.start_time}</span>
                <span className="font-medium">{b.title}</span>
                <span className="text-muted-foreground">{b.requester_name}{b.participants ? ` · ${b.participants} pers.` : ""}</span>
                <span className={`ml-auto rounded-full px-2 py-0.5 text-xs ${AUD_STATUS[b.status].soft}`}>{AUD_STATUS[b.status].label}</span>
                {b.amount != null && <span className="text-xs text-muted-foreground">{Number(b.amount).toFixed(2)} € · {AUD_PAY[b.payment_status]}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>

      <AuditoriumDialog open={dlg.open} onOpenChange={(o) => setDlg((d) => ({ ...d, open: o }))} booking={dlg.booking} defaultDate={dlg.date} defaultTime={dlg.time} onSaved={load} />
    </div>
  );
}
